<script setup lang="ts">
/**
 * The page a visitor lands on before they have an account.
 *
 * It is the pre-auth screen rather than a route, because this app has no router
 * and does not want one for two states: `App.vue`'s `auth` phase shows this, and
 * the Play button swaps in the sign-in panel. A signed-in player never sees it.
 *
 * ## Its own scroller
 *
 * `#app` is `height: 100%; overflow: hidden` — the arena is a canvas that must
 * never be scrolled by a stray gesture — so this page cannot rely on the window
 * scrolling. `.landing` is the scroll container, and every in-page anchor is
 * handled here against that element rather than left to the browser, which would
 * look for a scrollable `documentElement` and find none.
 *
 * ## Where the words and the numbers come from
 *
 * `landing-content.ts`, and almost all of it is imported from `@shared` rather
 * than written here — the modes are the wire's own names and blurbs, the
 * battlefields are `MAP_SUMMARIES`, the tick rate is `TICK_RATE`. This component
 * draws what it is handed and decides nothing about the game, which is what
 * stops the page and the product drifting apart. See that file's header.
 *
 * ## Everything visible degrades
 *
 * The hero falls back to a still, a missing map plate falls back to a plain
 * plate (the picker's own rule), and the footer hides whatever the CMS has no
 * answer for. Nothing on the way to the Play button can be blocked by a failed
 * fetch or an absent asset.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

import ItemPreview from '../garage/ItemPreview.vue';
import StatList from '../garage/StatList.vue';
import { HULL_STATS, TURRET_STATS, type StatDescriptor } from '../garage/stat-display';
import HeroTank from './HeroTank.vue';
import SiteHeader from './SiteHeader.vue';
import SiteFooter from './SiteFooter.vue';
import BrandLogo from '../ui/BrandLogo.vue';
import CurrencyIcon from '../ui/CurrencyIcon.vue';
import { pendingAnchor } from '../state/pending-anchor';
import {
  GROUND,
  HERO_FIGURES,
  HERO_TANK,
  HULL_PART,
  RANK_COUNT,
  RANK_FIRST,
  RANK_GROUPS,
  RANK_LAST,
  RESEARCH_EXAMPLE,
  RESEARCH_FACTS,
  SHOP_CEILING_RANK,
  SHOWCASE,
  SHOWCASE_REMAINDER,
  TURRET_PART,
  rankArt,
} from './landing-content';
import { fetchFaq, fetchShowcaseSpecs, type FaqEntry, type ShowcaseSpecRow } from './site-api';

const emit = defineEmits<{ play: [] }>();

const root = ref<HTMLElement | null>(null);
const openQuestion = ref<number | null>(0);

/**
 * Map plates that 404'd.
 *
 * The picker's own mechanism (`artFor` in `BattlePicker.vue`): a map without art
 * gets a plain plate rather than a broken image, so a battlefield added before
 * its picture exists still lists correctly. Recorded per id rather than hidden
 * per element so a plate that fails once is not retried on every re-render.
 */
const missingArt = ref(new Set<string>());

function plate(id: string): string | null {
  return missingArt.value.has(id) ? null : `/assets/maps/${id}.webp`;
}

function onArtError(id: string): void {
  missingArt.value = new Set(missingArt.value).add(id);
}

/**
 * Warms the browser's image cache for everything below the fold, on idle.
 *
 * The rank ladder (20 insignia), the three rank-group badges, the battlefield
 * gallery's plates and the research card's icon are all plain `<img
 * loading="lazy">`, so the browser only starts fetching each one once it
 * nears the viewport. A visitor scrolling faster than that fetch resolves
 * watches the picture pop in after its section is already on screen — which
 * is the thing being fixed here. This is the same `new Image()` trick
 * `GameHud`'s `warmResultArt` and `HeroTank`'s `prefetchOtherLiveries` already
 * use elsewhere in this app: it never blocks anything (`loading="lazy"` stays
 * on every tag, so nothing here competes with the hero's own models for
 * bandwidth), and by the time a section is actually scrolled to, its pictures
 * are already decoded and sitting in cache — the `<img>` tag's own request
 * resolves instantly rather than opening a fresh one.
 *
 * The three CSS `background-image` plates (`.art-layer--ground`,
 * `.art-layer--rank`, `.art-layer--research`) are warmed the same way. A
 * background image is not exempt from this — it is simply fetched at low
 * priority behind everything else on the page, which is the identical
 * "arrives right as you scroll past it" problem one layer down.
 */
const LANDING_WARM_ART: readonly string[] = [
  ...Array.from({ length: RANK_COUNT }, (_, i) => rankArt(i + 1)),
  RESEARCH_EXAMPLE.icon,
  ...GROUND.map((g) => `/assets/maps/${g.id}.webp`),
  '/assets/result/win.webp',
  '/assets/loading/1.webp',
  '/assets/landing/research-blueprint.webp',
];

function warmLandingArt(): void {
  const warm = () => {
    for (const src of LANDING_WARM_ART) {
      // Decoded into the image cache and dropped; the real `<img>` (or the
      // stylesheet, for the two backgrounds) asks for the same URL later and
      // gets it warm. Failure is silence — a picture that never arrives here
      // simply fetches cold when its own element eventually asks for it.
      const img = new Image();
      img.decoding = 'async';
      img.src = src;
    }
  };
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => void })
    .requestIdleCallback;
  // Safari has no `requestIdleCallback`, exactly as `warmResultArt` notes; a
  // timeout is the same intent one guarantee weaker.
  if (idle) idle(warm);
  else window.setTimeout(warm, 1000);
}

onMounted(() => {
  warmLandingArt();
});

/**
 * The battlefield gallery shows one map at a time, like the hull/turret
 * explainer shows one half at a time — a single, larger presentation reads
 * as a place worth looking at, where nine equal-sized tiles in a grid read as
 * a spec sheet.
 */
const groundIndex = ref(0);
const currentGround = computed(() => GROUND[groundIndex.value]);

function prevGround(): void {
  groundIndex.value = (groundIndex.value - 1 + GROUND.length) % GROUND.length;
}

function nextGround(): void {
  groundIndex.value = (groundIndex.value + 1) % GROUND.length;
}

/**
 * Real base stats for the ten showcase pairs — HP, damage, top speed and
 * reload, off `GET /api/garage/catalogue/spec`, the one corner of the garage
 * that answers a stranger. Keyed by `hullId+turretId` rather than an array in
 * `SHOWCASE`'s own order, so a card can look itself up without assuming the
 * response preserved that order.
 *
 * `null` — not an empty object — while the request is in flight or has
 * failed, and `showcaseStatsFor` reads that as "no bars yet" rather than
 * "confirmed zero", so a slow or failed fetch degrades to the plain card
 * (weight class, tier, gun) rather than a card full of empty tracks.
 */
const showcaseSpecs = ref<Record<string, ShowcaseSpecRow> | null>(null);

onMounted(() => {
  void fetchShowcaseSpecs(SHOWCASE.map((t) => ({ hullId: t.hullId, turretId: t.turretId }))).then(
    (rows) => {
      if (!rows) return;
      const byPair: Record<string, ShowcaseSpecRow> = {};
      for (const row of rows) byPair[`${row.hullId}+${row.turretId}`] = row;
      showcaseSpecs.value = byPair;
    },
  );
});

/**
 * The four stats a showcase card bars out, in the order a shopper reads
 * them: survivability, then the two firepower numbers, then the one where
 * lower is better. Pulled from the same `HULL_STATS`/`TURRET_STATS` the real
 * shop's own cards read — same labels, same units, same full-bar ceilings —
 * rather than a second, smaller set of descriptors invented for this page.
 */
const ALL_STAT_DESCRIPTORS: readonly StatDescriptor[] = [...HULL_STATS, ...TURRET_STATS];
function statDescriptor(key: string): StatDescriptor {
  const found = ALL_STAT_DESCRIPTORS.find((d) => d.key === key);
  if (!found) throw new Error(`no such stat descriptor: ${key}`);
  return found;
}
const SHOWCASE_STAT_DESCRIPTORS: StatDescriptor[] = [
  statDescriptor('maxHealth'),
  statDescriptor('damage'),
  statDescriptor('maxForwardSpeed'),
  statDescriptor('reloadSeconds'),
];

/** `null` while the fetch is pending or failed — see `showcaseSpecs`. */
function showcaseStatValues(hullId: string, turretId: string): Record<string, number> | null {
  const row = showcaseSpecs.value?.[`${hullId}+${turretId}`];
  if (!row) return null;
  return {
    maxHealth: row.maxHealth,
    damage: row.damage,
    maxForwardSpeed: row.maxForwardSpeed,
    reloadSeconds: row.reloadSeconds,
  };
}

/** Scrolls within this page's own container, since the document does not scroll. */
function goTo(href: string): void {
  const container = root.value;
  if (!container) return;
  if (href === '#top') {
    container.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  container.querySelector(href)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * `SiteHeader`/`SiteFooter` stash a section here when they are clicked from
 * somewhere that is not this page (the news routes) — see
 * `state/pending-anchor.ts`. This page is the only reader: once mounted,
 * its own sections exist, so this is the moment to finish the scroll that
 * could not be done from wherever the click actually happened.
 */
onMounted(() => {
  if (!pendingAnchor.value) return;
  const href = pendingAnchor.value;
  pendingAnchor.value = null;
  goTo(href);
});

function toggleQuestion(index: number): void {
  openQuestion.value = openQuestion.value === index ? null : index;
}

/** The admin-published FAQ rows — see `fetchFaq`'s own doc in `site-api.ts`. */
const faq = ref<FaqEntry[]>([]);
onMounted(() => {
  void fetchFaq().then((rows) => {
    faq.value = rows;
  });
});

/**
 * The lintel starts bare — no logo, no background — because the hero already
 * carries the wordmark, right below it; a second one in the header at scroll
 * position zero would say the same word twice before the visitor has moved a
 * pixel. `scrolled` is the one boolean both halves of the effect key off: the
 * hero logo's own shrink-and-fade out, and the lintel's background and logo
 * fading in. Driving both off one flag, flipped in one Vue tick, is what
 * keeps them arriving together rather than on two separately-timed scroll
 * thresholds — see `landing.css`'s Lintel section for the actual transitions,
 * and `SiteHeader.vue` for how the flag becomes a class.
 *
 * An `IntersectionObserver` rather than a scroll listener, for the reason
 * `groundArt`'s below is one: no per-frame work, and "has this left the
 * screen" is exactly its native question, where a scroll listener would need
 * its own throttling to ask the same thing cheaply.
 *
 * The observed target is the `<h1>` wrapper, not the logo it contains. The
 * logo is what animates (scale, translate, fade) on the way out, and
 * animating the very element the observer is measuring would feed that
 * transform back into the intersection calculation on every frame of its own
 * transition. The wrapper's layout box never moves, so there is nothing to
 * feed back — only the interior fade is transformed.
 *
 * `rootMargin`'s top edge is pulled down past the lintel's own height, so the
 * crossfade fires once the hero logo is actually hidden *behind* the bar rather
 * than a frame before it — a plain "entered/left the viewport" check would
 * trigger while the real logo is still peeking out from under the header.
 *
 * It is deliberately looser than any one bar rather than tuned to a number,
 * because there is no longer a single height to tune to: 65.9 px sticky on a
 * desktop, 55.4 px fixed below 64 rem. Erring long means the hero mark is
 * always gone before the header's arrives; erring short would show both at
 * once, which is the thing this whole mechanism exists to prevent.
 *
 * A browser with no `IntersectionObserver` gets the lintel exactly as it
 * always rendered — logo and background permanently on — rather than
 * stranding it in the bare state with no way out: a header whose identity
 * never arrives is worse than a plainer one, `BrandLogo`'s own missing-file
 * fallback argument one mechanism further along.
 */
const heroLogoRef = ref<HTMLElement | null>(null);
const scrolled = ref(!('IntersectionObserver' in window));
let headerObserver: IntersectionObserver | null = null;

onMounted(() => {
  const target = heroLogoRef.value;
  if (!target || !('IntersectionObserver' in window)) return;
  headerObserver = new IntersectionObserver(
    ([entry]) => {
      scrolled.value = !entry.isIntersecting;
    },
    { threshold: 0, rootMargin: '-80px 0px 0px 0px' },
  );
  headerObserver.observe(target);
});

onBeforeUnmount(() => {
  headerObserver?.disconnect();
  headerObserver = null;
});

/**
 * The maps section's background: a still image with a looping video faded in
 * on top of it once the video actually has frames on screen.
 *
 * The still is never removed — it is this element's own CSS `background`,
 * painted before any of this script runs — so a slow connection, a video
 * that 404s, a browser that refuses autoplay, or a visitor who has asked for
 * less motion all land on exactly the same frame this section always had.
 * The video is a pure addition, opacity 0 until `@playing` says otherwise.
 */
const groundArt = ref<HTMLElement | null>(null);
const groundVideo = ref<HTMLVideoElement | null>(null);
const groundVideoReady = ref(false);
const groundReducedMotion =
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

let groundObserver: IntersectionObserver | null = null;

onMounted(() => {
  if (groundReducedMotion) return;
  const art = groundArt.value;
  if (!art || !('IntersectionObserver' in window)) return;
  // Paused off-screen and resumed on return, the same courtesy the hero's
  // own WebGL scene extends the tab — a looping background nobody is
  // looking at should not keep decoding frames.
  groundObserver = new IntersectionObserver(
    (entries) => {
      const video = groundVideo.value;
      if (!video) return;
      if (entries.some((e) => e.isIntersecting)) void video.play().catch(() => undefined);
      else video.pause();
    },
    { threshold: 0 },
  );
  groundObserver.observe(art);
});

onBeforeUnmount(() => {
  groundObserver?.disconnect();
  groundObserver = null;
});

/**
 * The tank showcase's own interaction — drag-to-scroll, a coverflow lean
 * driven by scroll position, and a mouse-follow tilt on hover. All of it is
 * an enhancement over a row that already scrolls (native touch swipe,
 * scroll-snap, a keyboard-focusable "+more" link): nothing here is load
 * bearing, so a machine that skips all of it still has a working shelf.
 *
 * `prefers-reduced-motion` turns off the two effects that are pure motion —
 * the coverflow lean and the hover tilt — but not the drag, which is an input
 * method, not an animation.
 */
const showcaseRow = ref<HTMLElement | null>(null);
const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/**
 * Whether a mouse drag is currently live. Toggles `.showcase-row.dragging` in
 * `landing.css`, which is the actual fix for the drag having "snapped" rather
 * than tracked the pointer: `scroll-snap-type: x proximity` was still armed
 * *during* the drag, so every programmatic `scrollLeft` write on
 * `pointermove` was being pulled toward the nearest card boundary the moment
 * it came close to one, instead of following the cursor 1:1. Snapping is
 * meant to be the settle-after-release behaviour, not something fighting the
 * drag itself — so it is switched off for the drag's own duration and
 * restored the instant it ends.
 */
const showcaseDragging = ref(false);

let dragPointerId: number | null = null;
let dragStartX = 0;
let dragStartScroll = 0;
let dragMoved = false;

/**
 * Mouse only. Touch already has its own native drag-to-scroll (and its own
 * momentum and scroll-snap), and hijacking it with pointer events here would
 * fight the browser's own gesture recognition rather than add to it.
 */
function onShowcasePointerDown(event: PointerEvent): void {
  if (event.pointerType !== 'mouse') return;
  const row = showcaseRow.value;
  if (!row) return;
  dragPointerId = event.pointerId;
  dragStartX = event.clientX;
  dragStartScroll = row.scrollLeft;
  dragMoved = false;
  showcaseDragging.value = true;
  row.setPointerCapture(event.pointerId);
}

function onShowcasePointerMove(event: PointerEvent): void {
  if (dragPointerId === null || event.pointerId !== dragPointerId) return;
  const row = showcaseRow.value;
  if (!row) return;
  // Released outside the window entirely can drop the `pointerup` — the same
  // trap the research tree's own pan documents. A move that arrives with no
  // button held is the recovery: it ends the drag on the spot rather than
  // leaving the row captured to a button nobody is holding any more.
  if (event.buttons === 0) {
    endShowcaseDrag(event);
    return;
  }
  const dx = event.clientX - dragStartX;
  // A few pixels of slack before this counts as a drag rather than a click —
  // the same discipline the research tree's own pan uses, and for the same
  // reason: the "+more" link at the end of the row has to still be clickable.
  if (Math.abs(dx) > 4) dragMoved = true;
  if (dragMoved) row.scrollLeft = dragStartScroll - dx;
}

function endShowcaseDrag(event: PointerEvent): void {
  if (dragPointerId === null || event.pointerId !== dragPointerId) return;
  showcaseRow.value?.releasePointerCapture(dragPointerId);
  dragPointerId = null;
  showcaseDragging.value = false;
}

/**
 * Swallowed in the *capture* phase, before it ever reaches the "+more" link's
 * own click handler — a bubble-phase listener here would run after that
 * handler already fired. `dragMoved` is cleared afterwards either way, so a
 * genuine click (no drag at all) is untouched.
 */
function onShowcaseClickCapture(event: MouseEvent): void {
  if (dragMoved) {
    event.preventDefault();
    event.stopPropagation();
  }
  dragMoved = false;
}

let tiltFrame: number | null = null;

/**
 * The coverflow lean: each card rotates away from the row's own centre line
 * in proportion to how far off it sits, so the shelf reads as a curved rack
 * rather than a flat filmstrip. `--ry`/`--tz` are read by one shared
 * `transform` in `landing.css`, alongside the hover tilt's `--tilt-x/-y` —
 * one formula, so the two effects compose instead of one overwriting the
 * other's inline `style.transform`.
 */
function updateShowcaseTilt(): void {
  const row = showcaseRow.value;
  if (!row) return;
  const rowRect = row.getBoundingClientRect();
  const centre = rowRect.left + rowRect.width / 2;
  const half = rowRect.width / 2 || 1;
  for (const card of row.querySelectorAll<HTMLElement>('.tank-card')) {
    const r = card.getBoundingClientRect();
    const offset = Math.max(-1, Math.min(1, (r.left + r.width / 2 - centre) / half));
    card.style.setProperty('--ry', `${(-offset * 11).toFixed(2)}deg`);
    card.style.setProperty('--tz', `${(-Math.abs(offset) * 16).toFixed(1)}px`);
  }
}

function onShowcaseScroll(): void {
  if (reducedMotion) return;
  if (tiltFrame !== null) return;
  tiltFrame = requestAnimationFrame(() => {
    tiltFrame = null;
    updateShowcaseTilt();
  });
}

/**
 * Mouse only, for the reason drag is: a touch has no hover to begin with.
 *
 * Sets two independent things off the one cursor position: `--tilt-x`/
 * `--tilt-y` (the 3-D lean, read into the card's shared `transform` formula
 * in `landing.css`) and `--mx`/`--my` (where the cursor-follow sheen —
 * `.tank-card::before` — centres its highlight). The tilt range is wide on
 * purpose: a card that only nods a couple of degrees reads as jitter, not
 * as "the light is catching it".
 */
function onCardMouseMove(event: MouseEvent): void {
  const card = event.currentTarget as HTMLElement;
  const r = card.getBoundingClientRect();
  const px = (event.clientX - r.left) / r.width;
  const py = (event.clientY - r.top) / r.height;
  card.style.setProperty('--mx', `${(px * 100).toFixed(1)}%`);
  card.style.setProperty('--my', `${(py * 100).toFixed(1)}%`);
  if (reducedMotion) return;
  card.style.setProperty('--tilt-x', `${((py - 0.5) * -16).toFixed(2)}deg`);
  card.style.setProperty('--tilt-y', `${((px - 0.5) * 20).toFixed(2)}deg`);
}

function onCardMouseLeave(event: MouseEvent): void {
  const card = event.currentTarget as HTMLElement;
  card.style.setProperty('--tilt-x', '0deg');
  card.style.setProperty('--tilt-y', '0deg');
  card.style.setProperty('--mx', '50%');
  card.style.setProperty('--my', '50%');
}

let onShowcaseResize: (() => void) | null = null;

onMounted(async () => {
  if (reducedMotion) return;
  await nextTick();
  updateShowcaseTilt();
  onShowcaseResize = () => updateShowcaseTilt();
  window.addEventListener('resize', onShowcaseResize);
});

onBeforeUnmount(() => {
  if (onShowcaseResize) window.removeEventListener('resize', onShowcaseResize);
  if (tiltFrame !== null) cancelAnimationFrame(tiltFrame);
});
</script>

<template>
  <div ref="root" id="landing-root" class="landing" data-test="landing">
    <SiteHeader :scrolled="scrolled" @play="emit('play')" />

    <main id="top">
      <section class="hero">
        <HeroTank />

        <div class="hero-copy">
          <p class="eyebrow">Real-time armour · in the browser</p>
          <h1 ref="heroLogoRef" class="hero-title">
            <span class="sr-only">Steel of War — drive one way, aim another.</span>
            <BrandLogo
              class="hero-logo-title"
              :class="{ 'hero-logo-title-hidden': scrolled }"
              aria-hidden="true"
            />
          </h1>
          <div class="hero-cta">
            <button
              type="button"
              class="btn btn-solid"
              data-test="landing-play"
              @click="emit('play')"
            >
              Play free
            </button>
          </div>

          <ul class="oath">
            <li>Free to play</li>
            <li>No download</li>
            <li>Mouse, keyboard, touch</li>
          </ul>
        </div>

        <dl class="hero-figures">
          <div v-for="f in HERO_FIGURES" :key="f.label">
            <dt>{{ f.value }}</dt>
            <dd>{{ f.label }}</dd>
          </div>
        </dl>
      </section>

      <section id="showcase" class="band">
        <div class="wrap">
          <div class="shead">
            <h2>Build your own machine</h2>
            <p class="ssub">
              Every hull takes every turret — a light scout frame built for speed, a siege
              chassis built to stand still and hit hard, or anything in between. Pick the armour,
              pick the gun, and the strategy, the build and the looks are all yours to choose.
            </p>
          </div>

          <div class="showcase-viewport">
            <div
              ref="showcaseRow"
              class="showcase-row"
              :class="{ dragging: showcaseDragging }"
              @pointerdown="onShowcasePointerDown"
              @pointermove="onShowcasePointerMove"
              @pointerup="endShowcaseDrag"
              @pointercancel="endShowcaseDrag"
              @click.capture="onShowcaseClickCapture"
              @scroll="onShowcaseScroll"
            >
              <article
                v-for="tank in SHOWCASE"
                :key="`${tank.hullId}+${tank.turretId}`"
                class="tank-card"
                tabindex="0"
                @mousemove="onCardMouseMove"
                @mouseleave="onCardMouseLeave"
              >
                <div class="tank-card-art">
                  <ItemPreview
                    :hull-id="tank.hullId"
                    :turret-id="tank.turretId"
                    :paint-id="tank.paintId"
                    framing="tank"
                    :label="`${tank.hullName} hull, ${tank.turretName} turret`"
                  />
                </div>
                <div class="tank-card-body">
                  <h3>{{ tank.hullName }} <span class="tank-card-sep">/</span> {{ tank.turretName }}</h3>
                  <p class="tank-card-caption">
                    {{ tank.weightClass }} <span class="tank-card-sep">·</span> {{ tank.archetype }}
                  </p>
                  <StatList
                    v-if="showcaseStatValues(tank.hullId, tank.turretId)"
                    :descriptors="SHOWCASE_STAT_DESCRIPTORS"
                    :values="showcaseStatValues(tank.hullId, tank.turretId)!"
                    class="tank-card-stats"
                  />
                  <p class="tank-card-andmore">and more</p>
                </div>
              </article>

              <a
                class="tank-card tank-card-more"
                href="#"
                data-test="landing-showcase-more"
                @click.prevent="emit('play')"
                @mousemove="onCardMouseMove"
                @mouseleave="onCardMouseLeave"
              >
                <span class="tank-card-more-count">+{{ SHOWCASE_REMAINDER }}</span>
                <span class="tank-card-more-label">more in-game</span>
              </a>
            </div>
            <div class="showcase-fade showcase-fade-left" aria-hidden="true"></div>
            <div class="showcase-fade showcase-fade-right" aria-hidden="true"></div>
          </div>
        </div>
      </section>

      <section id="progression" class="band art-section">
        <div class="art-layer art-layer--rank" aria-hidden="true"></div>
        <div class="wrap">
          <div class="shead">
            <p class="stamp">{{ RANK_COUNT }} rungs</p>
            <h2>Rank climbs, the garage opens with it</h2>
            <p class="ssub">
              Twenty rungs, {{ RANK_FIRST }} to {{ RANK_LAST }}, earned by fighting rather than
              bought outright. Rank gates buying and never keeping — nothing you've already
              unlocked is taken back — and every promotion widens what you can build while the
              opposition gets a little tougher to match.
            </p>
          </div>

          <div class="rank-groups">
            <article v-for="group in RANK_GROUPS" :key="group.id" class="rank-group">
              <img :src="group.art" :alt="`Rank ${group.to} insignia`" loading="lazy" decoding="async" />
              <p class="rank-group-range">Ranks {{ group.from }}–{{ group.to }}</p>
              <h3>{{ group.name }}</h3>
              <p class="rank-group-mechanic">{{ group.mechanic }}</p>
              <p class="rank-group-combos">
                <span>{{ group.unlockValue }}</span> {{ group.unlockUnit }}
              </p>
            </article>
          </div>

          <ol class="ladder ladder-compact">
            <li v-for="n in RANK_COUNT" :key="n" :class="{ ceiling: n === SHOP_CEILING_RANK }">
              <img :src="rankArt(n)" :alt="`Rank ${n} insignia`" loading="lazy" decoding="async" />
              <span class="ladder-n">{{ n }}</span>
            </li>
          </ol>

          <p class="ladder-note">
            Rank {{ SHOP_CEILING_RANK }} is the deepest gate in the shop. The three rungs above
            it hand over a worn title instead of a vehicle — free identity is the one thing a
            rank above the power ceiling can give away without touching the balance the ceiling
            exists to protect.
          </p>
        </div>
      </section>

      <section id="thesis" class="band">
        <div class="wrap">
          <div class="shead">
            <h2>Two halves, one machine</h2>
            <p class="ssub">
              No tank comes in one piece. It’s a chassis and a gun, picked separately — and
              that pairing is what decides whether you’re built to run or built to hold ground.
            </p>
          </div>

          <div class="parts">
            <article class="part-block part-block--hull">
              <div class="part-art">
                <ItemPreview
                  :hull-id="HERO_TANK.hullId"
                  :turret-id="null"
                  :framing="HULL_PART.framing"
                  :label="HULL_PART.heading"
                />
              </div>
              <div class="part-text">
                <p class="part-kicker">{{ HULL_PART.heading }}</p>
                <p class="part-count">
                  <span class="part-count-n">{{ HULL_PART.count }}</span>
                  {{ HULL_PART.countLabel }}
                </p>
                <p class="part-body">{{ HULL_PART.body }}</p>
                <ul class="part-impacts">
                  <li v-for="tag in HULL_PART.impacts" :key="tag">{{ tag }}</li>
                </ul>
              </div>
            </article>

            <article class="part-block part-block--turret">
              <div class="part-text">
                <p class="part-kicker">{{ TURRET_PART.heading }}</p>
                <p class="part-count">
                  <span class="part-count-n">{{ TURRET_PART.count }}</span>
                  {{ TURRET_PART.countLabel }}
                </p>
                <p class="part-body">{{ TURRET_PART.body }}</p>
                <ul class="part-impacts">
                  <li v-for="tag in TURRET_PART.impacts" :key="tag">{{ tag }}</li>
                </ul>
              </div>
              <div class="part-art">
                <ItemPreview
                  :hull-id="null"
                  :turret-id="HERO_TANK.turretId"
                  :framing="TURRET_PART.framing"
                  :label="TURRET_PART.heading"
                />
              </div>
            </article>
          </div>
        </div>
      </section>

      <section id="ground" class="band art-section">
        <div ref="groundArt" class="art-layer art-layer--ground" aria-hidden="true">
          <video
            v-if="!groundReducedMotion"
            ref="groundVideo"
            class="art-video"
            :class="{ ready: groundVideoReady }"
            autoplay
            muted
            loop
            playsinline
            preload="auto"
            @playing="groundVideoReady = true"
          >
            <source src="/assets/result/win.webm" type="video/webm" />
            <source src="/assets/result/win.mp4" type="video/mp4" />
          </video>
        </div>
        <div class="wrap">
          <div class="shead">
            <p class="stamp">{{ GROUND.length }} battlefields</p>
            <h2>Ground that plays differently</h2>
            <p class="ssub">
              Every part of a map you can stand on, you can drive to — a test asserts it. Pick
              one in the garage; an arena keeps the map it was built with for its whole life.
            </p>
          </div>

          <div class="map-gallery">
            <article class="map-gallery-card">
              <div class="map-gallery-art">
                <img
                  v-if="plate(currentGround.id)"
                  :src="plate(currentGround.id)!"
                  :alt="`Overhead plan of ${currentGround.name}`"
                  loading="lazy"
                  decoding="async"
                  @error="onArtError(currentGround.id)"
                />
                <span v-else class="plate-blank" aria-hidden="true"></span>
              </div>
              <div class="ground-text">
                <p class="map-gallery-index">{{ groundIndex + 1 }} / {{ GROUND.length }}</p>
                <h3>{{ currentGround.name }}</h3>
                <p class="ground-blurb">{{ currentGround.blurb }}</p>
                <p class="ground-spec">
                  <span>{{ currentGround.size }}</span>
                  <span>{{ currentGround.side }}</span>
                  <span v-if="currentGround.bases">Fortified bases</span>
                </p>
              </div>
            </article>

            <div class="map-gallery-nav">
              <button
                type="button"
                class="map-gallery-btn"
                aria-label="Previous battlefield"
                @click="prevGround"
              >
                <span class="map-gallery-chevron prev" aria-hidden="true"></span>
              </button>
              <div class="map-gallery-dots">
                <button
                  v-for="(map, i) in GROUND"
                  :key="map.id"
                  type="button"
                  class="map-gallery-dot"
                  :class="{ active: i === groundIndex }"
                  :aria-current="i === groundIndex ? 'true' : undefined"
                  :aria-label="`Show ${map.name}`"
                  @click="groundIndex = i"
                ></button>
              </div>
              <button
                type="button"
                class="map-gallery-btn"
                aria-label="Next battlefield"
                @click="nextGround"
              >
                <span class="map-gallery-chevron next" aria-hidden="true"></span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <section id="research" class="band art-section">
        <div class="art-layer art-layer--research" aria-hidden="true"></div>
        <div class="wrap">
          <div class="research-grid">
            <div class="research-copy">
              <div class="shead">
                <p class="stamp">Gameplay progression</p>
                <h2>A tree you read, not a list you tick</h2>
                <p class="ssub">
                  Every hull, gun and part slot sits behind a research node, and the nodes are
                  drawn as an actual tree — pan it, zoom it, follow one lineage to the thing you
                  want. One node runs at a time, so every credit you spend on it is a decision
                  about what to have next rather than a checklist item. Nothing it unlocks is
                  ever taken back.
                </p>
              </div>
              <ul class="research-facts">
                <li v-for="fact in RESEARCH_FACTS" :key="fact.label">
                  <span class="research-fact-n">{{ fact.stat }}</span>
                  <span class="research-fact-label">{{ fact.label }}</span>
                </li>
              </ul>
            </div>
            <article class="research-card">
              <div class="research-card-icon">
                <img :src="RESEARCH_EXAMPLE.icon" :alt="RESEARCH_EXAMPLE.name" loading="lazy" decoding="async" />
              </div>
              <p class="research-card-branch">{{ RESEARCH_EXAMPLE.branch }} · Tier {{ RESEARCH_EXAMPLE.tier }}</p>
              <h3>{{ RESEARCH_EXAMPLE.name }}</h3>
              <p class="research-card-blurb">{{ RESEARCH_EXAMPLE.blurb }}</p>
              <p class="research-card-cost">
                <CurrencyIcon kind="credits" :labelled="false" />
                {{ RESEARCH_EXAMPLE.cost.toLocaleString() }}
              </p>
              <p class="research-card-note">No prerequisites — the tree's own root.</p>
            </article>
          </div>
        </div>
      </section>

      <section id="questions" class="band">
        <div class="wrap wrap-narrow">
          <div class="shead">
            <p class="stamp">Straight answers</p>
            <h2>Before you enlist</h2>
          </div>

          <div class="faq">
            <div v-for="(item, i) in faq" :key="item.question" class="faq-row">
              <h3>
                <button
                  type="button"
                  :aria-expanded="openQuestion === i"
                  @click="toggleQuestion(i)"
                >
                  <span>{{ item.question }}</span>
                  <i aria-hidden="true"></i>
                </button>
              </h3>
              <p v-show="openQuestion === i">{{ item.answer }}</p>
            </div>
          </div>
        </div>
      </section>

      <section class="finale">
        <div class="hazard" aria-hidden="true"></div>
        <div class="wrap">
          <p class="eyebrow">The bay is open</p>
          <h2>Your first tank is already in it</h2>
          <p class="lede">
            A Recruit hull, a Recruit gun, and enough credits to open four of the five research
            branches. Which one you leave shut is the first decision you make.
          </p>
          <button type="button" class="btn btn-solid btn-lg" @click="emit('play')">
            Play free
          </button>
        </div>
        <div class="hazard" aria-hidden="true"></div>
      </section>
    </main>

    <SiteFooter />
  </div>
</template>

<style src="./landing.css"></style>
